import { useCallback, useEffect, useState } from "react";
import { carouselItems } from "@/mocks/home";
import { getDetailRoute, getTypeLabel } from "@/utils/media";

const AUTOPLAY_MS = 5000;

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = carouselItems.length;

  const goTo = useCallback((index: number) => {
    setActive((index + total) % total);
  }, [total]);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % total);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  const current = carouselItems[active];

  return (
    <section
      className="relative w-full h-[520px] md:h-[620px] overflow-hidden bg-background-50"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="重点推荐"
    >
      {carouselItems.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${index === active ? "opacity-100" : "opacity-0"}`}
          aria-hidden={index !== active}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 天堂电影网推荐`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/40" />

      <div className="relative h-full w-full px-4 md:px-8 flex items-center">
        <div className="w-full max-w-2xl pt-16 md:pt-20">
          <div key={current.id} className="animate-fade-up">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                {getTypeLabel(current.type)}
              </span>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-accent-300">
                <i className="ri-star-fill text-accent-400" aria-hidden="true" />
                {current.rating.toFixed(1)}
              </span>
              <span className="text-sm text-foreground-400">{current.year}</span>
            </div>
            <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-wide text-foreground-950 leading-tight">
              {current.title}
            </h2>
            <p className="mt-3 text-lg md:text-xl text-accent-300 font-medium">{current.tagline}</p>
            <p className="mt-4 text-sm md:text-base text-foreground-400 leading-relaxed max-w-xl line-clamp-2">
              {current.description}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {current.genres.map((genre) => (
                <span
                  key={genre}
                  className="rounded-full border border-accent-500/40 bg-accent-500/10 px-3 py-1 text-xs text-accent-300"
                >
                  {genre}
                </span>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href={getDetailRoute(current.type, current.id)}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-fill text-lg" aria-hidden="true" />
                查看详情
              </a>
              <a
                href={`/category/${current.type}`}
                className="inline-flex items-center gap-2 rounded-md border border-foreground-400/40 px-6 py-3 text-sm font-medium text-foreground-800 hover:border-accent-400 hover:text-accent-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                进入{getTypeLabel(current.type)}频道
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 flex items-center gap-3">
        <button
          type="button"
          onClick={() => goTo(active - 1)}
          aria-label="上一张"
          className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300/60 bg-background-100/60 text-foreground-800 hover:border-accent-400 hover:text-accent-300 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => goTo(active + 1)}
          aria-label="下一张"
          className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300/60 bg-background-100/60 text-foreground-800 hover:border-accent-400 hover:text-accent-300 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl" aria-hidden="true" />
        </button>
      </div>

      <div className="absolute bottom-7 left-4 md:left-8 flex items-center gap-2">
        {carouselItems.map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={() => goTo(index)}
            aria-label={`切换到第 ${index + 1} 张`}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === active ? "w-10 bg-accent-500" : "w-4 bg-foreground-400/40 hover:bg-foreground-400/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}